import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import ImageUpload from './ImageUpload';

interface Props {
  data: any;
  onChange: (data: any) => void;
  websiteId?: string;
  domain?: string;
}

/** Human-readable labels for inner page hero sections */
const PAGE_LABELS: Record<string, string> = {
  services:      'Services page',
  about:         'About Us page',
  contact:       'Contact page',
  areas:         'Service Areas page',
  blog:          'Blog page',
  works:         'Past Projects page',
  financing:     'Financing page',
  emergencyTips: 'Emergency Tips page',
};

const ContentEditor: React.FC<Props> = ({ data, onChange, websiteId, domain }) => {
  const siteBaseUrl = domain ? `https://${domain}` : undefined;
  const form = data || {};

  const set = (path: string, value: any) => {
    const clone = JSON.parse(JSON.stringify(form));
    const keys = path.split('.');
    let obj = clone;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!obj[keys[i]]) obj[keys[i]] = {};
      obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = value;
    onChange(clone);
  };

  const get = (path: string) => path.split('.').reduce((o: any, k) => o?.[k], form) ?? '';

  const field = (label: string, path: string, opts?: { placeholder?: string; hint?: string }) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        value={get(path)}
        onChange={(e) => set(path, e.target.value)}
        placeholder={opts?.placeholder}
      />
      {opts?.hint && <p className="text-xs text-muted">{opts.hint}</p>}
    </div>
  );

  const textareaField = (label: string, path: string, rows = 3, hint?: string) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Textarea
        rows={rows}
        value={get(path)}
        onChange={(e) => set(path, e.target.value)}
      />
      {hint && <p className="text-xs text-muted">{hint}</p>}
    </div>
  );

  const pages = form?.pages || {};
  const pageKeys = ['services', 'about', 'contact', 'areas', 'blog', 'works', 'financing', 'emergencyTips'] as const;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-display font-semibold text-primary">Page Content</h2>
        <p className="text-sm text-muted mt-1 mb-5">Headlines, descriptions, and text shown to visitors</p>
      </div>

      {/* Hero section — the first thing visitors see */}
      <Card>
        <CardHeader>
          <CardTitle>Top Banner <span className="text-sm font-normal text-muted ml-1">(the first thing visitors see)</span></CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {field('Headline', 'hero.headline', { placeholder: 'e.g. Your Trusted Local Plumber', hint: 'Main large text at the top of your homepage' })}
          {field('Headline accent (highlighted word or phrase)', 'hero.headlineAccent', { placeholder: 'Max 5 words', hint: 'A short phrase shown in your accent color — e.g. "Fast & Reliable"' })}
          {textareaField('Subtext', 'hero.description', 3, 'One or two sentences below the headline describing what you do')}
          {websiteId && (
            <div className="space-y-1.5">
              <Label>Banner Photo</Label>
              <p className="text-xs text-muted">The large background or side image in the top banner</p>
              <ImageUpload
                websiteId={websiteId}
                currentUrl={get('hero.heroImage') || '/images/hero-technician.png'}
                path="hero"
                onUploaded={(url) => set('hero.heroImage', url)}
                onRemoved={() => set('hero.heroImage', '')}
                aspectRatio="aspect-[3/1]"
                siteBaseUrl={siteBaseUrl}
              />
            </div>
          )}
          {get('hero.heroImage') && (
            <div className="space-y-1.5">
              <Label>Image description (for accessibility)</Label>
              <Input
                value={get('hero.imageAlt')}
                onChange={(e) => set('hero.imageAlt', e.target.value)}
                placeholder="e.g. Plumber fixing a kitchen sink"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>About Us Section</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {textareaField('Description', 'aboutSection.description', 4, 'A short paragraph about your company shown in the About section')}
          {field('Mission Statement', 'aboutSection.mission', { hint: 'One sentence summarizing your company\'s mission or values' })}
          <div>
            <Label className="mb-2 block">Company Values (3 items)</Label>
            <p className="text-xs text-muted mb-2">Three short value cards shown in the About section</p>
            {(form?.aboutSection?.values || []).map((v: any, i: number) => (
              <div key={i} className="grid grid-cols-3 gap-2 mb-2">
                <Input
                  value={v.title || ''} onChange={(e) => {
                    const vals = [...(form.aboutSection?.values || [])];
                    vals[i] = { ...vals[i], title: e.target.value };
                    set('aboutSection.values', vals);
                  }}
                  placeholder="Value title"
                />
                <Input
                  value={v.description || ''} onChange={(e) => {
                    const vals = [...(form.aboutSection?.values || [])];
                    vals[i] = { ...vals[i], description: e.target.value };
                    set('aboutSection.values', vals);
                  }}
                  placeholder="Short description"
                />
                <Input
                  value={v.icon || ''} onChange={(e) => {
                    const vals = [...(form.aboutSection?.values || [])];
                    vals[i] = { ...vals[i], icon: e.target.value };
                    set('aboutSection.values', vals);
                  }}
                  placeholder="Icon name"
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>How It Works (Process Steps)</CardTitle></CardHeader>
        <CardContent>
          <p className="text-xs text-muted mb-3">Step-by-step guide shown to customers — e.g. "Call us → We arrive → Problem solved"</p>
          {(form?.processSection?.steps || []).map((step: any, i: number) => (
            <div key={i} className="grid grid-cols-3 gap-2 mb-2">
              <Input
                value={step.title || ''} onChange={(e) => {
                  const steps = [...(form.processSection?.steps || [])];
                  steps[i] = { ...steps[i], title: e.target.value };
                  set('processSection.steps', steps);
                }}
                placeholder="Step title"
              />
              <Input
                value={step.description || ''} onChange={(e) => {
                  const steps = [...(form.processSection?.steps || [])];
                  steps[i] = { ...steps[i], description: e.target.value };
                  set('processSection.steps', steps);
                }}
                placeholder="Description"
                className="col-span-2"
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Call-to-Action Banner</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted">The promotional strip shown mid-page encouraging visitors to call or book</p>
          {field('Headline', 'ctaBanner.headline')}
          {field('Subtext', 'ctaBanner.description')}
          {field('Button Text', 'ctaBanner.buttonText', { placeholder: 'e.g. Get a Free Quote' })}
        </CardContent>
      </Card>

      {/* Inner page headlines */}
      {pageKeys.some((key) => !!pages[key]) && (
        <Card>
          <CardHeader><CardTitle>Inner Page Headlines</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted mb-1">The headline and short description at the top of each inner page on your site</p>
            {pageKeys.map((key) =>
              pages[key] ? (
                <div key={key}>
                  <Label className="mb-1.5 block text-xs font-semibold tracking-wide uppercase text-muted">
                    {PAGE_LABELS[key] ?? key}
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      value={pages[key]?.headline || ''} onChange={(e) => set(`pages.${key}.headline`, e.target.value)}
                      placeholder="Headline"
                    />
                    <Input
                      value={pages[key]?.description || ''} onChange={(e) => set(`pages.${key}.description`, e.target.value)}
                      placeholder="Short description"
                    />
                  </div>
                </div>
              ) : null
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ContentEditor;
